import type { GeneralInfoPicklistItem } from '../api/generalInfoApi';
import { israelSubDistrictMatchesRegionLabel, localizeGeoLabel } from '../i18n/israelGeoLabels';
import { ISRAEL_COUNTRY_ID } from './assetCountryCurrency';
import type { IsraelSettlement } from './israelSettlements';
import { findIsraelSettlementByWire } from './israelSettlements';
import { buildAddressGeocodeQuery, formatAssetAddressStreetLine, type AddressGeocodeLabels } from './assetGeo';
import { applyPostalCodeToAssetBody } from './assetAddressPostalCode';
import { geocodeAddressDetails, type GeocodeResult } from './geocodeAddress';
import { setAtPath } from './cloneAndPath';

export type IsraelAddressGeoPatch = {
  countryId: number;
  stateId: number | null;
  zipCode: string | null;
};

function pickString(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim();
  }
  return '';
}

function regionLabelIsJudeaSamaria(label: string): boolean {
  const trimmed = label.trim();
  if (!trimmed) {
    return false;
  }
  const key = trimmed.toLowerCase();
  if (key.includes('judea') || key.includes('samaria') || key.includes('judaea')) {
    return true;
  }
  const he = localizeGeoLabel(trimmed, 'he');
  return he.includes('יהודה') || he.includes('שומרון');
}

/** Match API region picklist row from settlement sub-district (nafa) name. */
export function matchIsraelRegionId(
  regions: readonly GeneralInfoPicklistItem[],
  settlement: IsraelSettlement | null,
): number | null {
  const nafa = settlement?.nafaName?.trim();
  if (!nafa || regions.length === 0) {
    return null;
  }
  for (const region of regions) {
    const label = region.label.trim();
    if (!label) {
      continue;
    }
    if (israelSubDistrictMatchesRegionLabel(nafa, label)) {
      const id = Number(region.id);
      return Number.isFinite(id) ? id : null;
    }
  }
  // Official catalog lists some Judea and Samaria settlements under the Tulkarm sub-district.
  if (nafa === 'טול כרם' || nafa === 'טול-כרם') {
    for (const region of regions) {
      if (!regionLabelIsJudeaSamaria(region.label)) {
        continue;
      }
      const id = Number(region.id);
      if (Number.isFinite(id)) {
        return id;
      }
    }
  }
  return null;
}

export function resolveIsraelGeoFromCity(
  cityWire: string,
  regions: readonly GeneralInfoPicklistItem[],
): Pick<IsraelAddressGeoPatch, 'countryId' | 'stateId'> {
  const settlement = findIsraelSettlementByWire(cityWire);
  return {
    countryId: ISRAEL_COUNTRY_ID,
    stateId: matchIsraelRegionId(regions, settlement),
  };
}

export function buildIsraelAddressGeoLabels(
  regions: readonly GeneralInfoPicklistItem[],
  stateId: number | null,
  countries: readonly GeneralInfoPicklistItem[],
): AddressGeocodeLabels {
  const countryLabel =
    countries.find((row) => Number(row.id) === ISRAEL_COUNTRY_ID)?.label ?? 'Israel';
  const regionLabel =
    stateId != null
      ? regions.find((row) => Number(row.id) === stateId)?.label ?? null
      : null;
  return { countryLabel, regionLabel };
}

export async function geocodeIsraelAddressZip(
  address: Record<string, unknown>,
  labels?: AddressGeocodeLabels,
): Promise<string | null> {
  const hit = await geocodeAssetAddressDetails({ address }, labels);
  return hit?.postalCode?.trim() || null;
}

/** Geocode asset body with Israeli settlement disambiguation when city is from the official list. */
export async function geocodeAssetAddressDetails(
  body: Record<string, unknown>,
  labels?: AddressGeocodeLabels,
): Promise<GeocodeResult | null> {
  const addr = readAddressObject(body);
  const city = addr ? pickString(addr.city) : '';
  const settlement = city ? findIsraelSettlementByWire(city) : null;
  const streetLine = addr ? formatAssetAddressStreetLine(addr) : '';
  const query = buildAddressGeocodeQuery(body, labels);
  if (!query && !settlement) {
    return null;
  }
  return geocodeAddressDetails(query ?? '', {
    settlementHe: settlement?.he ?? null,
    settlementNafa: settlement?.nafaName ?? null,
    streetLine: streetLine || null,
  });
}

/** Apply country, optional region, and optional zip onto a root payload without overwriting zip. */
export function applyIsraelAddressGeoPatch(
  body: Record<string, unknown>,
  patch: IsraelAddressGeoPatch,
): Record<string, unknown> {
  let next = setAtPath(body, ['country_id'], patch.countryId);
  if (patch.stateId != null) {
    next = setAtPath(next, ['state_id'], patch.stateId);
  }
  if (patch.zipCode) {
    next = applyPostalCodeToAssetBody(next, patch.zipCode);
  }
  return next;
}

export function readAddressObject(body: Record<string, unknown>): Record<string, unknown> | null {
  const addr = body.address;
  if (addr !== null && typeof addr === 'object' && !Array.isArray(addr)) {
    return addr as Record<string, unknown>;
  }
  return null;
}

export function isIsraelAddressComplete(address: Record<string, unknown> | null): boolean {
  if (!address) {
    return false;
  }
  return Boolean(pickString(address.city) && pickString(address.street));
}
